import { matchIdentities, productIdentity, } from './identity.js';
/** What you would pay today — the promotion when there is one (API §4.2). */
function effectivePrice(item) {
    return item.promo_price_cents ?? item.price_cents ?? null;
}
/**
 * Group a ranked page of results into bundles, **preserving the ranking**: each
 * bundle appears where its best-ranked member did, and a row that bundles with
 * nothing comes back as a bundle of one. Every input row is present in exactly
 * one bundle.
 *
 * `options.categories` is the household's own vocabulary
 * (`categoryVocabulary`), which is what lets one shop name the category and the
 * other not. Omit it and that relaxation never fires.
 */
export function bundleByProduct(results, options = {}) {
    const shelves = new Map();
    /** Rows with no usable identity, kept in place as bundles of one. */
    const alone = [];
    results.forEach((item, index) => {
        const found = productIdentity(item);
        if (!found.ok) {
            alone.push({ item, index });
            return;
        }
        const id = found.identity;
        const groups = shelves.get(id.shelf) ?? [];
        // The shelf key already holds brand, package form and size, so the only
        // question left for a member of this shelf is the product words — which is
        // the one gate `matchIdentities` still has to run.
        const existing = groups.find((g) => matchIdentities(g.identity, id, options).match);
        if (existing) {
            existing.members.push({ item, index });
            existing.bare &&= id.bare;
        }
        else {
            groups.push({ identity: id, rank: index, members: [{ item, index }], bare: id.bare });
        }
        shelves.set(id.shelf, groups);
    });
    /*
     * The bare-title rule, and the guard on it.
     *
     * `Nutella 600 g` has to find `Nutella Hazelnootpasta 600 g`, so a group
     * whose members all said nothing beyond brand and size joins the described
     * one. It joins only when there is exactly **one** thing it could be: with
     * two described groups on the shelf a bare title is genuinely ambiguous, and
     * guessing between them is the wrong-bundle failure this module is bent away
     * from. `bare` is computed before line words are dropped, so `Alpro Original`
     * never travels on this rule.
     *
     * The shelf now includes the package form, which is what stops this rule
     * doing what it did with `COCA-COLA`: a brand-only Lidl title can still only
     * join products of its own packaging, so a bottle never joins a four-pack.
     */
    for (const groups of shelves.values()) {
        const bare = groups.filter((g) => g.bare && g.identity.distinct.length === 0);
        const described = groups.filter((g) => !bare.includes(g));
        if (bare.length === 0 || described.length !== 1)
            continue;
        const target = described[0];
        for (const group of bare) {
            target.members.push(...group.members);
            target.rank = Math.min(target.rank, group.rank);
            groups.splice(groups.indexOf(group), 1);
        }
    }
    const bundles = alone.map(({ item, index }) => ({
        rank: index,
        bundle: {
            lead: item,
            title: item.name,
            offers: [{ store: item.store, item, price_cents: effectivePrice(item) }],
            cheapest: null,
            family: null,
        },
    }));
    for (const groups of shelves.values()) {
        for (const group of groups) {
            group.members.sort((a, b) => a.index - b.index);
            const lead = group.members[0].item;
            /*
             * One offer per shop. A shop that returned the same product twice — Albert
             * Heijn does, with two rows for one carton — is one price on this row, not
             * two: the bundle's whole claim is "here is what each shop charges", and a
             * shop quoted twice is not an answer to that. The cheaper of the two is
             * the one kept, which is also the one somebody comparing prices meant.
             */
            const perStore = new Map();
            for (const { item } of group.members) {
                const price = effectivePrice(item);
                const held = perStore.get(item.store);
                if (!held || (price !== null && (held.price_cents === null || price < held.price_cents))) {
                    perStore.set(item.store, { store: item.store, item, price_cents: price });
                }
            }
            const offers = [...perStore.values()].sort((a, b) => {
                // A shop that published no price sorts last rather than cheapest — a
                // missing price is not a low one (ADAPTERS §9.3, most of Lidl's food).
                if (a.price_cents === null)
                    return b.price_cents === null ? 0 : 1;
                if (b.price_cents === null)
                    return -1;
                return a.price_cents - b.price_cents;
            });
            /*
             * Cheapest, only where that word is true.
             *
             * It needs two shops to compare, a price to compare with, and a runner-up
             * that is genuinely dearer. The tie is not an edge case: the same litre of
             * Oatly is €2,89 at both Albert Heijn and Jumbo, so a flag handed to
             * whichever sorted first would be the app making up a recommendation on a
             * screen somebody is reading to decide where to shop.
             *
             * **A runner-up with no price is not a dearer one.** This used to read
             * `runnerUp.price_cents === null || runnerUp.price_cents > best`, which
             * contradicted the paragraph above it: a shop that published nothing was
             * counted as beaten. So Albert Heijn at €2,89 beside a Lidl row with no
             * price was badged *goedkoopst* — a comparison against an unknown,
             * presented as a result, in the one place the app is asked which shop to
             * walk to. Unknown is not high, exactly as `offers` sorting it last is not
             * a claim that it is dear. It already fired on the Lidl rows priced only
             * for cardholders, and Lidl's in-store range (LIDL_CATALOGUE_PLAN §6.1)
             * makes that the common case rather than the rare one.
             */
            const best = offers[0];
            const runnerUp = offers[1];
            const cheapest = best && runnerUp && best.price_cents !== null && runnerUp.price_cents !== null &&
                runnerUp.price_cents > best.price_cents
                ? best.store
                : null;
            bundles.push({
                rank: group.rank,
                bundle: {
                    lead,
                    title: titleFor(group),
                    offers,
                    cheapest,
                    family: group.identity.family,
                },
            });
        }
    }
    bundles.sort((a, b) => a.rank - b.rank);
    return bundles.map((b) => b.bundle);
}
/**
 * The most informative of the members' titles.
 *
 * "Best answer to the query" and "best name for a row" are different
 * questions, and the lead answers the first. Here the measure is how many words
 * the title spends on the product itself — brand and size excluded, since every
 * member shares those — so `Nutella Hazelnootpasta` beats `Nutella 600 g` and
 * neither the longest title nor the marketing-heaviest one wins by being long.
 * Ties fall back to the best-ranked member, so this can only ever change a row
 * that had a genuinely more descriptive name available.
 */
function titleFor(group) {
    let best = group.members[0];
    let bestWords = -1;
    for (const member of group.members) {
        const id = productIdentity(member.item);
        const words = id.ok ? id.identity.core.length : 0;
        if (words > bestWords) {
            best = member;
            bestWords = words;
        }
    }
    return best.item.name;
}
//# sourceMappingURL=bundle.js.map